(function () {
  'use strict';

  if (window.customElements && customElements.get('free-gift-progress')) return;

  const SELECTORS = {
    bar: '[data-free-gift-bar]',
    status: '[data-free-gift-status]',
    hint: '[data-free-gift-hint]',
    count: '[data-free-gift-count]',
    claim: '[data-free-gift-claim]',
    claimLabel: '[data-free-gift-claim-label]',
    claimed: '[data-free-gift-claimed]',
    modal: '[data-free-gift-modal]',
    modalClose: '[data-free-gift-modal-close]',
    form: '[data-free-gift-form]',
    variantInput: '[data-free-gift-variant-input]',
    option: '[data-free-gift-option]',
    submit: '[data-free-gift-submit]',
    error: '[data-free-gift-error]',
    variantsScript: '[data-free-gift-variants]',
    media: '[data-free-gift-media]',
    cartItem: '[data-item][data-product-id]',
  };

  const CLASSES = {
    unlocked: 'is-unlocked',
    claimed: 'is-claimed',
    modalOpen: 'is-open',
    bodyLocked: 'free-gift-modal-open',
  };

  const CHANGE_URL = (window.theme && window.theme.routes && window.theme.routes.cart_change_url) || '/cart/change.js';
  const ADD_URL = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add.js';

  class FreeGiftProgress extends HTMLElement {
    constructor() {
      super();

      this.threshold = parseInt(this.dataset.threshold, 10) || 10;
      this.qualifyingTag = (this.dataset.qualifyingTag || 'charms').toLowerCase();
      this.giftProductId = parseInt(this.dataset.giftProductId, 10) || 0;
      this.giftHasVariants = this.dataset.giftHasVariants === 'true';
      this.defaultVariantId = parseInt(this.dataset.giftDefaultVariantId, 10) || 0;
      this.progressTemplate = this.dataset.progressTemplate || "You're [count] [item] away from a Free Bracelet!";
      this.unlockedMessage = this.dataset.unlockedMessage || 'Congratulations! You unlocked a free bracelet.';
      this.claimLabel = this.dataset.claimLabel || 'Claim Bracelet';
      this.claimedLabel = this.dataset.claimedLabel || 'Bracelet added to your cart';

      this.qualifyingCount = parseInt(this.dataset.qualifyingCount, 10) || 0;
      this.giftLineKey = this.dataset.giftLineKey || '';
      this.giftInCart = !!this.giftLineKey;

      this.handleCartChange = this.handleCartChange.bind(this);
      this.handleClaimClick = this.handleClaimClick.bind(this);
      this.handleModalClose = this.handleModalClose.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleOptionChange = this.handleOptionChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.barFill = this.querySelector(SELECTORS.bar);
      this.statusEl = this.querySelector(SELECTORS.status);
      this.hintEl = this.querySelector(SELECTORS.hint);
      this.countEl = this.querySelector(SELECTORS.count);
      this.claimBtn = this.querySelector(SELECTORS.claim);
      this.claimedEl = this.querySelector(SELECTORS.claimed);
      this.modal = this.querySelector(SELECTORS.modal);
      this.form = this.querySelector(SELECTORS.form);
      this.variantInput = this.querySelector(SELECTORS.variantInput);
      this.errorEl = this.querySelector(SELECTORS.error);
      this.submitBtn = this.querySelector(SELECTORS.submit);

      const variantsScript = this.querySelector(SELECTORS.variantsScript);
      if (variantsScript) {
        try {
          this.variants = JSON.parse(variantsScript.textContent);
        } catch (err) {
          this.variants = [];
        }
      } else {
        this.variants = [];
      }

      // Teleport the modal to <body> so it escapes the cart drawer's
      // stacking context (transforms / overflow break position: fixed).
      if (this.modal && this.modal.parentNode !== document.body) {
        document.body.appendChild(this.modal);
      }

      // Query media AFTER teleport (modal now lives on body, and children
      // are not available in the constructor).
      this.modalMedia = this.modal
        ? this.modal.querySelector(SELECTORS.media)
        : this.querySelector(SELECTORS.media);

      if (this.modal) {
        this.modal.addEventListener('click', (event) => {
          if (event.target.matches(SELECTORS.modalClose)) {
            this.closeModal();
          }
        });

        this.modal.querySelectorAll(SELECTORS.option).forEach((input) => {
          input.addEventListener('change', this.handleOptionChange);
        });
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handleFormSubmit);
        this.updateVariantFromOptions();
      }

      if (this.claimBtn) {
        this.claimBtn.addEventListener('click', this.handleClaimClick);
      }

      document.addEventListener('theme:cart:change', this.handleCartChange);
      document.addEventListener('theme:product:added', this.handleCartChange);
      document.addEventListener('keydown', this.handleKeydown);

      this.recomputeFromCartItems();
      this.render();

      // Reconcile any inconsistent state from a previous session.
      this.maybeAutoRemoveGift();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:change', this.handleCartChange);
      document.removeEventListener('theme:product:added', this.handleCartChange);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    handleCartChange() {
      this.recomputeFromCartItems();
      this.render();
      this.maybeAutoRemoveGift();
    }

    /**
     * Read the rendered cart items to figure out qualifying count and gift state.
     */
    recomputeFromCartItems() {
      const items = document.querySelectorAll(SELECTORS.cartItem);
      let qualifying = 0;
      let giftKey = '';

      items.forEach((item) => {
        const isQualifying = item.dataset.qualifyingItem === 'true';
        const isFreeGift = item.dataset.freeGiftLine === 'true';
        const qty = parseInt(item.dataset.lineQuantity, 10) || 0;

        if (isFreeGift) {
          giftKey = item.dataset.lineKey || '';
        } else if (isQualifying) {
          qualifying += qty;
        }
      });

      this.qualifyingCount = qualifying;
      this.giftLineKey = giftKey;
      this.giftInCart = !!giftKey;
    }

    render() {
      const remaining = Math.max(0, this.threshold - this.qualifyingCount);
      const unlocked = this.qualifyingCount >= this.threshold;
      const percent = Math.min(100, (this.qualifyingCount / this.threshold) * 100);

      this.classList.toggle(CLASSES.unlocked, unlocked);
      this.classList.toggle(CLASSES.claimed, this.giftInCart);

      if (this.barFill) {
        this.barFill.style.setProperty('--free-gift-progress', percent + '%');
      }

      this.setAttribute('aria-valuenow', this.qualifyingCount);

      const displayCount = Math.min(this.qualifyingCount, this.threshold);

      if (this.countEl) {
        this.countEl.textContent = displayCount;
      }

      if (this.hintEl) {
        this.hintEl.innerHTML =
          '<span data-free-gift-count>' +
          displayCount +
          '</span> / ' +
          this.threshold +
          ' ' +
          this.qualifyingTag;
      }

      if (this.statusEl) {
        if (unlocked) {
          this.statusEl.textContent = this.unlockedMessage;
        } else {
          const word = remaining === 1 ? this.singularTagWord() : this.qualifyingTag;
          const message = this.progressTemplate
            .replace('[count]', remaining)
            .replace('[item]', word);
          this.statusEl.textContent = message;
        }
      }

      this.renderClaimUI(unlocked);
    }

    renderClaimUI(unlocked) {
      if (unlocked && !this.claimBtn) {
        this.claimBtn = document.createElement('button');
        this.claimBtn.type = 'button';
        this.claimBtn.className = 'btn btn--primary btn--solid free-gift__claim';
        this.claimBtn.setAttribute('data-free-gift-claim', '');
        const label = document.createElement('span');
        label.setAttribute('data-free-gift-claim-label', '');
        label.textContent = this.claimLabel;
        this.claimBtn.appendChild(label);
        this.claimBtn.addEventListener('click', this.handleClaimClick);

        const panel = this.querySelector('.free-gift__panel');
        if (panel) panel.appendChild(this.claimBtn);
      }

      if (this.claimBtn) {
        if (unlocked && !this.giftInCart) {
          this.claimBtn.hidden = false;
          this.claimBtn.disabled = false;
        } else {
          this.claimBtn.hidden = true;
          this.claimBtn.disabled = true;
        }
      }

      if (!this.claimedEl && unlocked) {
        this.claimedEl = document.createElement('p');
        this.claimedEl.className = 'free-gift__claimed';
        this.claimedEl.setAttribute('data-free-gift-claimed', '');
        this.claimedEl.innerHTML = '<span>' + this.claimedLabel + '</span>';
        const panel = this.querySelector('.free-gift__panel');
        if (panel) panel.appendChild(this.claimedEl);
      }

      if (this.claimedEl) {
        if (this.giftInCart && this._giftJustClaimed) {

          this.claimedEl.hidden = false;
          this.claimedEl.style.transition = 'opacity 0.6s ease';
          this.claimedEl.style.opacity = '1';

          clearTimeout(this._claimedFadeTimer);
          this._claimedFadeTimer = setTimeout(() => {
            this._giftJustClaimed = false;  // reset HERE, after fade starts
            this.claimedEl.style.opacity = '0';
            setTimeout(() => {
              this.claimedEl.hidden = true;
              this.claimedEl.style.opacity = '1';
            }, 600);
          }, 2000);

        } else if (!this._giftJustClaimed) {
          this.claimedEl.hidden = true;
        }
      }
    }

    singularTagWord() {
      if (this.qualifyingTag.endsWith('s')) {
        return this.qualifyingTag.slice(0, -1);
      }
      return this.qualifyingTag;
    }

    handleClaimClick(event) {
      event.preventDefault();
      if (this.giftInCart) return;

      if (this.giftHasVariants) {
        this.openModal();
      } else {
        this.addGiftToCart(this.defaultVariantId);
      }
    }

    openModal() {
      if (!this.modal) return;
      this.modal.hidden = false;
      this.modal.classList.add(CLASSES.modalOpen);
      document.body.classList.add(CLASSES.bodyLocked);

      // Make sure the image matches the currently selected swatch on open.
      this.updateVariantFromOptions();

      // Focus the close button for keyboard accessibility
      const closeBtn = this.modal.querySelector('.free-gift-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove(CLASSES.modalOpen);
      document.body.classList.remove(CLASSES.bodyLocked);

      // Hide after the animation
      setTimeout(() => {
        if (!this.modal.classList.contains(CLASSES.modalOpen)) {
          this.modal.hidden = true;
        }
      }, 250);
    }

    handleModalClose(event) {
      event.preventDefault();
      this.closeModal();
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.modal && this.modal.classList.contains(CLASSES.modalOpen)) {
        this.closeModal();
      }
    }

    handleOptionChange() {
      this.updateVariantFromOptions();
    }

    /**
     * Match selected option values to the right variant id and swap the image.
     */
    updateVariantFromOptions() {
      if (!this.variants || !this.variants.length || !this.modal) return;

      const fieldsets = this.modal.querySelectorAll('[data-free-gift-option-index]');
      if (!fieldsets.length) return;

      const selectedOptions = [];
      fieldsets.forEach((fieldset) => {
        const idx = parseInt(fieldset.dataset.freeGiftOptionIndex, 10);
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        selectedOptions[idx] = checked ? checked.value : null;
      });

      const match = this.variants.find((variant) => {
        const variantOptions = [variant.option1, variant.option2, variant.option3];
        return selectedOptions.every((value, idx) => {
          if (value === null || value === undefined) return true;
          return variantOptions[idx] === value;
        });
      });

      if (match && this.variantInput) {
        this.variantInput.value = match.id;
        if (this.submitBtn) {
          this.submitBtn.disabled = !match.available;
          this.submitBtn.textContent = match.available
            ? this.claimLabel
            : 'Sold out';
        }
        this.swapModalImage(match);
      }
    }

    /**
     * Resolve a variant's image src from either featured_image or featured_media.
     */
    variantImageSrc(variant) {
      if (variant.featured_image && variant.featured_image.src) {
        return { src: variant.featured_image.src, alt: variant.featured_image.alt };
      }
      if (
        variant.featured_media &&
        variant.featured_media.preview_image &&
        variant.featured_media.preview_image.src
      ) {
        return { src: variant.featured_media.preview_image.src, alt: variant.featured_media.alt };
      }
      return null;
    }

    /**
     * Update the modal's main image to the matched variant's image.
     */
    swapModalImage(variant) {
      if (!this.modalMedia || !variant) return;

      const resolved = this.variantImageSrc(variant);
      if (!resolved) return;

      let src = resolved.src;
      try {
        const u = new URL(src, window.location.href);
        u.searchParams.set('width', '600');
        src = u.toString();
      } catch (e) {}

      let img = this.modalMedia.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.className = 'free-gift-modal__image';
        img.loading = 'lazy';
        this.modalMedia.appendChild(img);
      }

      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;
      if (resolved.alt) img.alt = resolved.alt;
    }

    handleFormSubmit(event) {
      event.preventDefault();
      if (!this.form) return;

      const variantId = this.variantInput ? this.variantInput.value : this.defaultVariantId;
      if (!variantId) return;

      this.addGiftToCart(variantId);
    }

    addGiftToCart(variantId) {
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.classList.add('is-loading');
      }
      if (this.claimBtn) {
        this.claimBtn.disabled = true;
        this.claimBtn.classList.add('is-loading');
      }
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('properties[_free_gift]', 'yes');

      fetch(ADD_URL, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
        },
        body: formData,
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            throw new Error((data && (data.description || data.message)) || 'Unable to add gift.');
          }

          this.giftInCart = true;
          this._giftJustClaimed = true;
          if (data && data.key) {
            this.giftLineKey = data.key;
          }

          this.closeModal();
          this.requestCartRefresh();
        })
        .catch((err) => {
          if (this.errorEl) {
            this.errorEl.hidden = false;
            this.errorEl.textContent = err.message || 'Could not add free gift.';
          }
        })
        .finally(() => {
          if (this.submitBtn) {
            this.submitBtn.disabled = false;
            this.submitBtn.classList.remove('is-loading');
          }
          if (this.claimBtn) {
            this.claimBtn.disabled = false;
            this.claimBtn.classList.remove('is-loading');
          }
        });
    }

    /**
     * If the gift sits in the cart but qualifying items dropped below threshold,
     * remove the gift line automatically.
     */
    maybeAutoRemoveGift() {
      if (!this.giftInCart || !this.giftLineKey) return;
      if (this.qualifyingCount >= this.threshold) return;

      this.removeGiftLine(this.giftLineKey);
    }

    removeGiftLine(key) {
      const removingKey = key || this.giftLineKey;
      if (!removingKey) return;

      const previousKey = this.giftLineKey;
      this.giftLineKey = '';
      this.giftInCart = false;

      fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: removingKey, quantity: 0 }),
      })
        .then((response) => response.json())
        .then(() => {
          this.requestCartRefresh();
        })
        .catch((err) => {
          // Restore state on failure to avoid getting out of sync.
          this.giftLineKey = previousKey;
          this.giftInCart = !!previousKey;
          console.error('[free-gift] Failed to auto-remove gift', err);
        });
    }

    /**
     * Trigger the existing cart re-render flow so the line items update.
     */
    requestCartRefresh() {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    }
  }

  customElements.define('free-gift-progress', FreeGiftProgress);
})();
